import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSupplierApplications, importProcurement, unrejectSupplierApplication, deleteSupplierApplication, updateSupplierContact } from '../../../models/procurementModel';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

function SupplierApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editContactData, setEditContactData] = useState({ email: '', phone: '' });
  const [editingContactProcessing, setEditingContactProcessing] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Load initial pagination state from localStorage, or use defaults
  const initialPagination = () => {
    const storedPagination = localStorage.getItem('supplierApplicationsTablePagination');
    if (storedPagination) {
      return JSON.parse(storedPagination);
    }
    return {
      pageIndex: 0,
      pageSize: 20,
    };
  };

  // Server-side state
  const [pagination, setPagination] = useState(initialPagination);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('0'); // Default to pending applications
  const [activeTab, setActiveTab] = useState('pending'); // Tab state: 'pending', 'more_info', 'rejected'
  const [sorting, setSorting] = useState([{ id: 'application_date', desc: true }]);
  const [rowSelection, setRowSelection] = useState({}); // For bulk operations

  // Save pagination state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('supplierApplicationsTablePagination', JSON.stringify(pagination));
  }, [pagination]);

  const handleOpenEditContact = (application) => {
    setEditingApplication(application);
    setEditContactData({
      email: application.E_Mail || '',
      phone: application.Mobile_Phone || ''
    });
    setShowEditContactModal(true);
  };

  const handleSaveContact = async () => {
    if (!editingApplication) return;

    try {
      setEditingContactProcessing(true);
      const bpCode = String(editingApplication.BP_Code || editingApplication.application_bp_code || '');

      // Prepare update data with all required fields
      // Only send non-empty values to avoid database type errors
      const updateData = {
        BP_Code: bpCode,
        E_Mail: editContactData.email,
        Mobile_Phone: editContactData.phone,
        BP_Name: editingApplication.BP_Name,
        Contact_Person: editingApplication.Contact_Person,
        Group_Code: editingApplication.Group_Code || null,
        Company_Reg: editingApplication.Company_Reg || null,
        BBBEE_Type: editingApplication.BBBEE_Type || null,
        BBBEE_Level: editingApplication.BBBEE_Level || null,
        BBBEE_Expiration_Date: editingApplication.BBBEE_Expiration_Date || null,
        COIDA_Certificate_Number: editingApplication.COIDA_Certificate_Number || null,
        COIDA_Certificate_Expiry_Date: editingApplication.COIDA_Certificate_Expiry_Date || null,
        Tax_Certification_Number: editingApplication.Tax_Certification_Number || null,
        Tax_Certificate_Expiry_Date: editingApplication.Tax_Certificate_Expiry_Date || null,
        VendorID: editingApplication.VendorID || null,
        Status: editingApplication.Status || null
      };

      await updateSupplierContact(bpCode, updateData);

      // Update local state
      setApplications(prev =>
        prev.map(app =>
          (String(app.BP_Code) === bpCode || String(app.application_bp_code) === bpCode)
            ? { ...app, E_Mail: editContactData.email, Mobile_Phone: editContactData.phone }
            : app
        )
      );

      toast.success('Contact information updated successfully');
      setShowEditContactModal(false);
      setEditingApplication(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact information');
    } finally {
      setEditingContactProcessing(false);
    }
  };

  // Memoize columns
  const columns = useMemo(() => {
    const baseColumns = [];
    
    // Add checkbox column for rejected tab
    if (activeTab === 'rejected') {
      baseColumns.push({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        ),
        enableSorting: false,
      });
    }
    
    baseColumns.push(
      {
        accessorKey: 'application_date',
        header: 'Application Date',
        cell: info => <span className="text-gray-700">{formatDate(info.getValue())}</span>,
        enableSorting: true,
      },
      {
        accessorKey: 'application_status',
        header: 'Status',
        cell: info => {
          const status = info.getValue();
          if (status === '2' || status === 2) {
            return (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                More Info Requested
              </span>
            );
          }
          if (status === '7' || status === 7) {
            return (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Rejected
              </span>
            );
          }
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Pending Review
            </span>
          );
        },
        enableSorting: true,
      },
      // Hide these columns in rejected tab
      ...(activeTab !== 'rejected' ? [
        {
          accessorKey: 'BP_Name',
          header: 'Trading Name',
          cell: info => {
            const value = info.getValue() || 'N/A';
            return <span className="text-gray-700">{value}</span>;
          },
          enableSorting: true,
        },
        {
          accessorKey: 'Contact_Person',
          header: 'Contact Person',
          cell: info => {
            const value = info.getValue() || 'N/A';
            return <span className="text-gray-700">{value}</span>;
          },
          enableSorting: true,
        },
        {
          accessorKey: 'E_Mail',
          header: 'Email',
          cell: info => {
            const value = info.getValue();
            const row = info.row.original;
            return (
              <div className="flex flex-col gap-2">
                <div className="min-w-0">
                  {value ? (
                    <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 break-all text-sm">
                      {value}
                    </a>
                  ) : (
                    <span className="text-gray-700 text-sm">N/A</span>
                  )}
                </div>
                <button
                  onClick={() => handleOpenEditContact(row)}
                  type="button"
                  className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-medium whitespace-nowrap transition-colors w-fit"
                  title="Edit email and phone"
                >
                  Edit
                </button>
              </div>
            );
          },
          enableSorting: true,
        },
        {
          accessorKey: 'Mobile_Phone',
          header: 'Mobile Phone',
          cell: info => {
            const value = info.getValue() || 'N/A';
            return <span className="text-gray-700">{value}</span>;
          },
          enableSorting: true,
        },
      ] : []),
      {
        id: 'actions',
        header: () => <div className="text-center">Actions</div>,
        cell: ({ row }) => {
          const status = row.original.application_status;
          const isRejected = status === '7' || status === 7;
          const bpCode = String(row.original.BP_Code || row.original.application_bp_code || '');
          
          return (
            <div className="flex items-center justify-center gap-2">
              {isRejected ? (
                <>
                  <button
                    onClick={() => handleDelete(bpCode)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs flex items-center gap-1.5 relative"
                    title="Delete Application and Supplier - This will permanently delete the application and supplier record"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Delete</span>
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full border border-white animate-pulse"></span>
                  </button>
                  <button
                    onClick={() => handleUnreject(bpCode)}
                    className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
                    title="Unreject Application"
                  >
                    Unreject
                  </button>
                </>
              ) : (
                bpCode && (
                  <Link
                    to={`/dashboard/procurement/new_supplier/${bpCode}`}
                    className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                    title="View Details"
                  >
                    Details
                  </Link>
                )
              )}
            </div>
          );
        },
        enableSorting: false,
      }
    );
    
    return baseColumns;
  }, [activeTab]);

  const fetchApplications = useCallback(async () => {
    console.log('fetchApplications called');
    setLoading(true);
    try {
      // Prepare API parameters
      const params = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter || '',
      };
      
      // Add status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      // Add sorting parameters
      if (sorting.length > 0) {
        const sort = sorting[0];
        params.sortBy = sort.id;
        params.sortOrder = sort.desc ? 'DESC' : 'ASC';
      }
      
      console.log('API Request params:', params);
      console.log('Current statusFilter:', statusFilter);
      
      const response = await getSupplierApplications(params);
      console.log('Supplier applications response:', response);
      
      if (response && response.data) {
        console.log('Response data length:', response.data.length);
        console.log('Response pagination:', response.pagination);
        setApplications(response.data || []);
        setPaginationInfo(response.pagination || null);
        setTotalRecords(response.pagination?.total || 0);
      } else {
        // Fallback for non-paginated response
        console.log('Using fallback response handling');
        setApplications(response || []);
        setTotalRecords(response?.length || 0);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load supplier applications: ' + error.message);
      toast.error('Failed to fetch supplier applications');
      setApplications([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, statusFilter, sorting]);

  // Debounced search function
  const handleSearch = useCallback((searchValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setGlobalFilter(searchValue);
      setPagination(p => ({ ...p, pageIndex: 0 })); // Reset to first page on search
    }, 300); // 300ms debounce
  }, []);

  // Handler to reset filters
  const handleResetFilters = useCallback(() => {
    setGlobalFilter('');
    setStatusFilter('0'); // Reset to pending instead of all
    setActiveTab('pending');
    setPagination(p => ({ ...p, pageIndex: 0 }));
    // Clear search input
    const searchInput = document.querySelector('input[placeholder="Search applications..."]');
    if (searchInput) {
      searchInput.value = '';
    }
  }, []);

  // Handler for unrejecting an application
  const handleUnreject = async (reference) => {
    if (!reference) {
      toast.error('Application reference is missing');
      return;
    }

    if (!window.confirm('Are you sure you want to unreject this application? It will be visible again in the pending applications list.')) {
      return;
    }

    try {
      await unrejectSupplierApplication(reference);
      toast.success('Application unrejected successfully');
      // Refresh the applications list
      fetchApplications();
    } catch (error) {
      console.error('Error unrejecting application:', error);
      toast.error(error.response?.data?.message || 'Failed to unreject application');
    }
  };

  // Handler for deleting an application
  const handleDelete = async (reference) => {
    if (!reference) {
      toast.error('Application reference is missing');
      return;
    }

    if (!window.confirm('Are you sure you want to permanently delete this application and supplier? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteSupplierApplication(reference);
      toast.success('Application and supplier deleted successfully');
      // Refresh the applications list
      fetchApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error(error.response?.data?.message || 'Failed to delete application');
    }
  };

  // Handler for bulk deleting applications
  const handleBulkDelete = async () => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) {
      toast.error('Please select at least one application to delete');
      return;
    }

    const references = selectedRows.map(row => {
      return row.original.BP_Code || row.original.application_bp_code;
    }).filter(ref => ref);

    if (references.length === 0) {
      toast.error('No valid applications selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${references.length} application(s) and supplier(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      // Delete all selected applications
      const deletePromises = references.map(ref => deleteSupplierApplication(ref));
      await Promise.all(deletePromises);
      toast.success(`${references.length} application(s) deleted successfully`);
      // Clear selection and refresh
      setRowSelection({});
      fetchApplications();
    } catch (error) {
      console.error('Error bulk deleting applications:', error);
      toast.error(error.response?.data?.message || 'Failed to delete some applications');
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setRowSelection({}); // Clear selection when changing tabs
    if (tab === 'pending') {
      setStatusFilter('0');
    } else if (tab === 'more_info') {
      setStatusFilter('2');
    } else if (tab === 'rejected') {
      setStatusFilter('7');
    }
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const table = useReactTable({
    data: applications,
    columns,
    state: {
      pagination,
      globalFilter,
      sorting,
      rowSelection,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => activeTab === 'rejected', // Only enable selection for rejected tab
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Server-side pagination
    manualFiltering: true,  // Server-side filtering
    manualSorting: true,    // Server-side sorting
    pageCount: paginationInfo ? paginationInfo.last_page : Math.ceil(totalRecords / pagination.pageSize),
    debugTable: false,
  });

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      setImporting(true);
      await importProcurement(importFile);
      toast.success('File imported successfully');
      setImportFile(null);
      setShowImportForm(false);
      fetchApplications();
    } catch (error) {
      console.error('Error importing file:', error);
      toast.error('Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-GB');
  };

  // Render loading state separately for clarity
  if (loading && applications.length === 0) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">New Supplier Applications</h1>
                <p className="text-xs text-gray-500 mb-1">Review and manage new supplier registration applications</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {paginationInfo ? 
                      `Showing ${paginationInfo.from} to ${paginationInfo.to} of ${paginationInfo.total} results` : 
                      totalRecords > 0 ? `${totalRecords} results` : '0 results'
                    }
                    {(globalFilter || statusFilter !== '0') && (
                      <span className="ml-1 text-blue-600">(filtered)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={fetchApplications}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                

                {/* Page Size Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => {
                      setPagination(prev => ({
                        ...prev,
                        pageSize: Number(e.target.value),
                        pageIndex: 0
                      }));
                    }}
                    className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 25, 50, 100].map(size => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </select>
                </div>

                {/* Import Button */}
                <button
                  onClick={() => setShowImportForm(!showImportForm)}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Import
                </button>
              </div>
              
              {/* Row 2: Search Box */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={globalFilter ?? ''}
                  onChange={e => handleSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search applications..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4">
        {/* Tabs for different application statuses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between">
              <nav className="flex -mb-px" aria-label="Tabs">
                <button
                  onClick={() => handleTabChange('pending')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'pending'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Review
                </button>
                <button
                  onClick={() => handleTabChange('more_info')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'more_info'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  More Info Requested
                </button>
                <button
                  onClick={() => handleTabChange('rejected')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'rejected'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Rejected
                </button>
              </nav>
              {/* Bulk delete button for rejected tab */}
              {activeTab === 'rejected' && table.getSelectedRowModel().rows.length > 0 && (
                <div className="px-4 py-2">
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Selected ({table.getSelectedRowModel().rows.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Import Form */}
        {showImportForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Import Supplier Applications</h3>
          <form onSubmit={handleImport} className="space-y-4">
            <div>
              <label htmlFor="file" className="block text-sm font-medium text-gray-700">
                Select CSV File
              </label>
              <input
                type="file"
                id="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowImportForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={importing}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Applications Table */}
      {loading ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading applications...</span>
          </div>
        </div>
      ) : applications.length === 0 ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <p className="text-gray-500">No supplier applications found.</p>
        </div>
      ) : (
        <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className={`px-3 py-2 text-left text-xs font-semibold uppercase ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                        style={{ color: '#0087D4' }}
                        onClick={header.column.getToggleSortingHandler()}
                        title={header.column.getCanSort() ? (header.column.getNextSortingOrder() === 'asc' ? 'Sort ascending' : header.column.getNextSortingOrder() === 'desc' ? 'Sort descending' : 'Clear sort') : undefined}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <span className="ml-1 text-blue-600">↑</span>,
                          desc: <span className="ml-1 text-blue-600">↓</span>,
                        }[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && applications.length > 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center">
                      <p className="text-gray-600">
                        {globalFilter ? "No applications found matching your search." : "No applications found"}
                      </p>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.map(row => {
                  const status = row.original.application_status;
                  const isMoreInfoRequested = status === '2' || status === 2;
                  const isPendingTab = activeTab === 'pending';
                  // Highlight rows with more info requested in pending tab
                  const highlightClass = isMoreInfoRequested && isPendingTab 
                    ? 'bg-orange-50 border-l-4 border-orange-400 hover:bg-orange-100' 
                    : 'hover:bg-blue-50';
                  
                  return (
                    <tr key={row.id} className={`${highlightClass} transition-colors`}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-2 text-sm text-gray-700">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Enhanced Pagination */}
          {applications.length > 0 && (
            <div className="px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" style={{ backgroundColor: 'rgba(0, 135, 212, 0.02)' }}>
              <div className="flex items-center gap-4">
                <div className="text-gray-500">
                  {paginationInfo ? 
                    `Showing ${paginationInfo.from} to ${paginationInfo.to} of ${paginationInfo.total} results` : 
                    `Showing ${applications.length} results`
                  }
                </div>
                
                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Show:</span>
                  <select
                    value={pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 20, 50, 100].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-500">per page</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  First
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const totalPages = table.getPageCount();
                    const currentPage = pagination.pageIndex + 1;
                    const pageNumbers = [];
                    
                    // Show max 5 page numbers
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    
                    // Adjust start if we're near the end
                    if (endPage - startPage < 4) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => table.setPageIndex(i - 1)}
                          className={`px-3 py-1.5 border rounded text-xs font-medium transition-colors ${
                            i === currentPage
                              ? 'text-white border-transparent'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                          style={i === currentPage ? { backgroundColor: '#0087D4', borderColor: '#0087D4' } : {}}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    return pageNumbers;
                  })()}
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Next
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40 rounded text-xs font-medium transition-colors"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showEditContactModal && editingApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Contact Information</h3>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveContact();
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={editContactData.email}
                  onChange={(e) => setEditContactData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  value={editContactData.phone}
                  onChange={(e) => setEditContactData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+27 12 345 6789"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditContactModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editingContactProcessing}
                  className="flex-1 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                >
                  {editingContactProcessing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default SupplierApplications;
